/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Upload, FileText, Trash2, ArrowLeft, Building2, Briefcase, Mail, Phone } from 'lucide-react';
import { api } from '../api';
import { Badge } from '../components/ui';

const DOC_TYPES = ['KYC', 'Certificate', 'Contract', 'Filing', 'Draft', 'Correspondence', 'Other'];

function DocRow({ doc, onDelete, onOpen }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/[0.02] border border-white/[0.06] hover:border-amber-500/20 group" data-testid={`doc-${doc.id}`}>
      <div className="w-9 h-9 rounded-lg bg-sky-500/10 border border-sky-500/20 flex items-center justify-center">
        <FileText size={14} className="text-sky-300" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-slate-100 text-[13px] truncate">{doc.name}</div>
        <div className="text-slate-500 text-[10px] mt-0.5 flex items-center gap-2">
          <span className="uppercase tracking-wider">{doc.docType || 'Other'}</span>
          <span>·</span>
          <span>{Math.round((doc.sizeBytes || 0) / 1024)} KB</span>
        </div>
      </div>
      <button onClick={() => onOpen(doc)} className="text-[11px] text-amber-200/70 hover:text-amber-100 border border-white/[0.08] hover:border-amber-500/30 rounded-full px-3 py-1">Open</button>
      <button onClick={() => onDelete(doc)} className="text-slate-500 hover:text-rose-300 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={13} /></button>
    </div>
  );
}

function DocUploader({ clientId, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [docType, setDocType] = useState('KYC');
  const upload = async (file) => {
    setBusy(true); setErr('');
    try {
      const init = await api.post(`/clients/${clientId}/documents/init-upload`, {
        filename: file.name, contentType: file.type, sizeBytes: file.size, docType,
      });
      const put = await fetch(init.data.uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
      if (!put.ok) throw new Error(`S3 upload rejected (${put.status})`);
      await new Promise((r) => setTimeout(r, 600));
      const fin = await api.post(`/clients/${clientId}/documents/finalize`, {
        s3Key: init.data.s3Key, mediaId: init.data.mediaId, filename: file.name, contentType: file.type, docType,
      });
      onUploaded(fin.data);
    } catch (x) { setErr(x?.response?.data?.detail || x.message || 'Upload failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="cl-doc-uploader">
      <select value={docType} onChange={(e) => setDocType(e.target.value)}
              className="bg-white/[0.03] border border-white/[0.08] rounded-full px-3 py-1.5 text-[11px] text-slate-200 outline-none focus:border-amber-500/40">
        {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      <label className="inline-flex items-center gap-1.5 text-[11px] text-amber-950 bg-amber-400 hover:bg-amber-300 rounded-full px-3 py-1.5 cursor-pointer font-semibold">
        <Upload size={12} strokeWidth={2.5} /> {busy ? 'Uploading…' : 'Add document'}
        <input type="file" className="hidden" disabled={busy}
               accept="application/pdf,image/jpeg,image/png,image/webp,.docx,.xlsx,.doc,.xls,.txt,.csv"
               onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
      </label>
      {err && <div className="text-rose-300 text-[11px] bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1 w-full">{err}</div>}
    </div>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [entities, setEntities] = useState([]);
  const [matters, setMatters] = useState([]);
  const [ctxDraft, setCtxDraft] = useState('');
  const [savingCtx, setSavingCtx] = useState(false);

  const load = () => Promise.all([
    api.get(`/clients/${id}`),
    api.get('/contacts', { params: { clientId: id } }),
    api.get('/entities', { params: { clientId: id } }),
    api.get('/matters'),
  ]).then(([c, ct, e, m]) => {
    setClient(c.data);
    setCtxDraft(c.data.context || '');
    setContacts(ct.data.items);
    setEntities(e.data.items);
    setMatters(m.data.items.filter((x) => x.clientId === id));
  });
  useEffect(() => { load(); }, [id]);

  const saveContext = async () => {
    setSavingCtx(true);
    try {
      const r = await api.patch(`/clients/${id}/context`, { context: ctxDraft });
      setClient(r.data);
    } finally { setSavingCtx(false); }
  };
  const openDoc = async (doc) => {
    const r = await api.get(`/clients/${id}/documents/${doc.id}/url`);
    if (r.data.signedUrl) window.open(r.data.signedUrl, '_blank', 'noopener');
  };
  const deleteDoc = async (doc) => {
    if (!confirm(`Delete "${doc.name}"?`)) return;
    await api.delete(`/clients/${id}/documents/${doc.id}`);
    load();
  };

  if (!client) return <div className="text-slate-500 text-sm">Loading client…</div>;

  const primary = contacts.find((c) => c.id === client.primaryContactId);
  const dirty = ctxDraft !== (client.context || '');

  return (
    <div data-testid="liq-client-detail">
      <div className="mb-4">
        <Link to="/liq/clients" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-amber-200">
          <ArrowLeft size={13} /> Clients
        </Link>
      </div>

      <div className="flex items-start gap-4 mb-6">
        <div className="w-14 h-14 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
          <Building2 size={22} className="text-emerald-300" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.32em] text-amber-200/60 mb-1">{client.type}</div>
          <h1 className="font-serif text-[28px] leading-tight text-slate-50">{client.clientName}</h1>
          {primary && (
            <div className="flex items-center gap-4 mt-2 text-[12px] text-slate-400">
              <span className="text-slate-100">{primary.fullName}</span>
              <span className="inline-flex items-center gap-1"><Mail size={11} />{primary.email}</span>
              {primary.phone && <span className="inline-flex items-center gap-1"><Phone size={11} />{primary.phone}</span>}
            </div>
          )}
        </div>
        <Badge tone={client.status === 'Active' ? 'emerald' : 'slate'}>{client.status}</Badge>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-5">
        {/* LEFT — context + documents */}
        <div className="space-y-5">
          {/* Context */}
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-5" data-testid="cl-context">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.24em] text-amber-200/70">About {client.clientName.split(' ')[0]}</div>
              {dirty && (
                <button onClick={saveContext} disabled={savingCtx}
                  className="text-[11px] text-amber-950 bg-amber-400 hover:bg-amber-300 rounded-full px-3 py-1 font-semibold"
                  data-testid="cl-save-context">{savingCtx ? 'Saving…' : 'Save'}</button>
              )}
            </div>
            <textarea rows={5} value={ctxDraft} onChange={(e) => setCtxDraft(e.target.value)}
              placeholder="What does this company do? Sector, size, jurisdiction, key products, founders, funding stage, any critical background the agent should know before touching any matter…"
              className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-[13px] text-slate-100 placeholder-slate-500 outline-none focus:border-amber-500/40 resize-none"
              data-testid="cl-context-text" />
            <div className="text-[10px] text-slate-600 mt-1">This context is loaded into any agent chat when a matter for this company is attached.</div>
          </div>

          {/* Documents */}
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-5" data-testid="cl-docs">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.24em] text-amber-200/70">
                Company Documents · {(client.documents || []).length}
              </div>
              <DocUploader clientId={id} onUploaded={load} />
            </div>
            {(client.documents || []).length === 0 ? (
              <div className="text-slate-600 text-[12px] italic py-4 text-center">
                No documents yet. Upload KYC, incorporation docs or any reference file the agents should be able to read.
              </div>
            ) : (
              <div className="space-y-1.5">
                {(client.documents || []).map((d) => (
                  <DocRow key={d.id} doc={d} onOpen={openDoc} onDelete={deleteDoc} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — matters + entities */}
        <div className="space-y-4">
          {/* Matters */}
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-5" data-testid="cl-matters">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.24em] text-amber-200/70">Matters · {matters.length}</div>
              <Link to={`/liq/matters?client=${id}`} className="text-[11px] text-amber-200/70 hover:text-amber-100">Create matter →</Link>
            </div>
            {matters.length === 0 ? (
              <div className="text-slate-600 text-[12px] italic py-3">No matters yet.</div>
            ) : (
              <div className="space-y-1.5">
                {matters.map((m) => (
                  <Link key={m.id} to={`/liq/matters/${m.id}`}
                    className="block p-2.5 rounded-lg bg-white/[0.02] border border-white/[0.04] hover:border-amber-500/30 hover:bg-amber-500/[0.03]">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] font-mono text-amber-200/80">{m.matterCode}</div>
                        <div className="text-[13px] text-slate-100 truncate">{m.title}</div>
                      </div>
                      {m.blocked && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-500/15 border border-rose-500/40 text-rose-300">Blocked</span>}
                    </div>
                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-500/10 border border-sky-500/30 text-sky-300">{m.stage}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Entities */}
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-5">
            <div className="text-[10px] uppercase tracking-[0.24em] text-amber-200/70 mb-3">Entities · {entities.length}</div>
            {entities.length === 0 ? (
              <div className="text-slate-600 text-[12px] italic">None</div>
            ) : entities.map((en) => (
              <div key={en.id} className="flex items-center gap-2 py-1.5 text-[12px]">
                <Briefcase size={12} className="text-emerald-300/70" />
                <div className="flex-1 min-w-0 truncate text-slate-200">{en.legalName}</div>
                <span className="text-slate-500 text-[10px]">{en.entityType}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
